import { useState, useRef, useEffect } from 'react';
import type { User } from '../api';
import { logout } from '../api';
import './UserMenu.css';

interface UserMenuProps {
  user: User;
  onLogout: () => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function UserMenu({ user, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      await logout();
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      alert('Error cerrando sesión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="user-menu" ref={ref}>
      <button
        ref={triggerRef}
        className="user-menu-trigger"
        onClick={() => setOpen(!open)}
        aria-label="Menú de usuario"
        aria-haspopup="menu"
        aria-expanded={open}
        title={user.displayName}
      >
        <div className="user-avatar">{getInitials(user.displayName)}</div>
        <span className="user-menu-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="user-menu-dropdown" role="menu" aria-label="Menú de usuario">
          <div className="user-menu-header">
            <div className="user-menu-header-top">
              <div className="user-avatar">{getInitials(user.displayName)}</div>
              <div className="user-menu-header-info">
                <span className="user-menu-header-name">{user.displayName}</span>
                <span className="user-menu-header-email">{user.email}</span>
              </div>
            </div>
            <div className="user-menu-id">{user.id}</div>
          </div>

          <div className="user-menu-jira">
            <span>🔗</span>
            <span>
              Conectado con <strong>Jira Cloud</strong>
            </span>
          </div>

          <div className="user-menu-section">
            <div className="user-menu-section-title">Configuración</div>
            <button className="user-menu-item" role="menuitem" disabled aria-disabled="true">
              <span className="user-menu-item-icon">⚙️</span>
              <span>Preferencias</span>
              <span className="user-menu-item-badge">Próximamente</span>
            </button>
            <button className="user-menu-item" role="menuitem" disabled aria-disabled="true">
              <span className="user-menu-item-icon">🔔</span>
              <span>Notificaciones</span>
              <span className="user-menu-item-badge">Próximamente</span>
            </button>
          </div>

          <div className="user-menu-section">
            <button
              className="user-menu-item danger"
              role="menuitem"
              onClick={handleLogout}
              disabled={isLoading}
            >
              <span className="user-menu-item-icon">🚪</span>
              <span>{isLoading ? 'Cerrando sesión...' : 'Cerrar sesión'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
